import {
  deleteAquiferPumpingColumn,
  updateAquifer,
  updateAquiferColumn,
  renameOutletPoint,
} from "../model/projectEntities.ts";
import type { LoadedProject } from "../model/project.ts";
import { isSeriesScaleLocked, type DailySeries } from "../model/dailySeries.ts";
import type { AemetProvenance } from "../model/storage.ts";
import type { Aquifer, EvalHidProject, OutletPoint, SubBasin } from "../model/types.ts";
import type { SubBasinLayer } from "../model/gisLayer.ts";
import { EditSubBasinDialog } from "./editor/EditSubBasinDialog.tsx";
import { EditAquiferDialog } from "./AquiferCatalog.tsx";
import { EditOutletPointDialog } from "./OutletPointCatalog.tsx";

/**
 * The 3 entity edit dialogs (sub-basin/outlet point/aquifer), byte-identical
 * between SubBasinMap.tsx and SubBasinCanvas.tsx before this — both views
 * let clicking an entity open its dialog directly (see CLAUDE.md's "Editar
 * subcuencas/puntos de desagüe/acuíferos desde el mapa"). Only the dialogs
 * themselves are shared here; which entity is being edited stays owned by
 * whichever view is mounted (see useEntityEditDialogs.ts's own comment on
 * why the raw codes can't move here too).
 */
export function EntityEditDialogs({
  project,
  series,
  gisLayer,
  aemetProvenance,
  onRecordAemetProvenance,
  onChangeProject,
  onChangeSeries,
  editingSubBasinObj,
  editingOutletObj,
  editingAquiferObj,
  onCloseSubBasin,
  onCloseOutlet,
  onCloseAquifer,
  observedFlow,
  onChangeObservedFlow,
  calibrationMode,
}: {
  project: EvalHidProject;
  series: LoadedProject["series"];
  gisLayer?: SubBasinLayer | null;
  aemetProvenance?: Record<number, AemetProvenance>;
  onRecordAemetProvenance?: (subBasinCode: number, provenance: AemetProvenance) => void;
  onChangeProject?: (project: EvalHidProject) => void;
  onChangeSeries?: (series: LoadedProject["series"]) => void;
  editingSubBasinObj: SubBasin | undefined;
  editingOutletObj: OutletPoint | undefined;
  editingAquiferObj: Aquifer | undefined;
  onCloseSubBasin: () => void;
  onCloseOutlet: () => void;
  onCloseAquifer: () => void;
  observedFlow?: DailySeries | null;
  onChangeObservedFlow?: (series: DailySeries | null) => void;
  calibrationMode?: boolean;
}) {
  return (
    <>
      {editingSubBasinObj && onChangeProject && onChangeSeries && (
        <EditSubBasinDialog
          project={project}
          subBasin={editingSubBasinObj}
          series={series}
          gisLayer={gisLayer}
          aemetProvenance={aemetProvenance?.[editingSubBasinObj.subBasinCode]}
          onRecordAemetProvenance={onRecordAemetProvenance}
          onChangeProject={onChangeProject}
          onChangeSeries={onChangeSeries}
          onClose={onCloseSubBasin}
        />
      )}
      {editingOutletObj && onChangeProject && (
        <EditOutletPointDialog
          initialName={editingOutletObj.name}
          onSave={(name) => {
            onChangeProject(renameOutletPoint(project, editingOutletObj.outletCode, name));
            onCloseOutlet();
          }}
          onClose={onCloseOutlet}
          observedFlow={observedFlow}
          onChangeObservedFlow={calibrationMode ? onChangeObservedFlow : undefined}
          scale={project.seriesScale}
        />
      )}
      {editingAquiferObj && onChangeProject && (
        <EditAquiferDialog
          initial={{
            name: editingAquiferObj.name,
            vo: editingAquiferObj.vo ?? 0,
            alfa: editingAquiferObj.alfa ?? 0,
            outletCode: editingAquiferObj.outletCode,
          }}
          outletPoints={project.outletPoints}
          pumpingSeries={series.pumping}
          onChangePumpingSeries={
            onChangeSeries ? (next) => onChangeSeries({ ...series, pumping: next }) : undefined
          }
          pumpingCol={editingAquiferObj.pumpingCol}
          scale={project.seriesScale}
          scaleLocked={isSeriesScaleLocked(series)}
          onChangePumpingIndex={
            onChangeSeries
              ? (index, inferredScale) => {
                  let p = updateAquiferColumn(project, editingAquiferObj.aquiferCode, index);
                  if (inferredScale) p = { ...p, seriesScale: inferredScale };
                  onChangeProject(p);
                }
              : undefined
          }
          onDeleteSeries={
            onChangeSeries && series.pumping
              ? () => {
                  const result = deleteAquiferPumpingColumn(
                    project,
                    series.pumping!,
                    editingAquiferObj.aquiferCode
                  );
                  return {
                    sharedWithOthers: result.sharedWithOthers,
                    confirm: () => {
                      onChangeProject(result.project);
                      onChangeSeries({ ...series, pumping: result.series });
                    },
                  };
                }
              : undefined
          }
          onSave={(changes) => {
            onChangeProject(updateAquifer(project, editingAquiferObj.aquiferCode, changes));
            onCloseAquifer();
          }}
          onClose={onCloseAquifer}
        />
      )}
    </>
  );
}
